import { useEffect, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { t } from 'i18next'
import { SearchIcon } from 'src/components/shared/ui/Icons'
import LexnetIconButton from 'src/components/shared/ui/form/fields/LexnetIconButton'
import ProcedimientoView from 'src/components/views/EscritosLexnet/components/shared/components/Procedimiento/ProcedimientoView'
import RemoveInfoButton from 'src/components/views/EscritosLexnet/components/shared/components/RemoveInfoButton'
import useClearFormSections from 'src/components/views/EscritosLexnet/components/shared/hooks/useClearFormSections'
import {
  ProcedimientoFormInitialvalues,
  ProcedimientoFormValues
} from 'src/components/views/EscritosLexnet/components/shared/types/procedimientoTypes'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { Orden, TipoMensaje } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import useNewModal from 'src/hooks/useModal'

function Procedimiento(): JSX.Element {
  const [disabledRemoveButton, setDisabledRemoveButton] = useState(true)
  const { values, setFieldValue } = useFormikContext<ProcedimientoFormValues>()
  const { clearProcedimientoFieldset } = useClearFormSections()
  const ProcedimientoDescripcionField = values[FormField.PROCEDIMIENTO_DESCRIPCION]
  const { tipoMensaje } = useTipoMensajeContext()
  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )
  const [ordenOrigen] = useField<Orden>(
    `${FormField.ORGANO_ORIGEN}.${FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL}`
  )

  const {
    isOpened: isProcedimientoOpened,
    close: closeProcedimiento,
    open: openProcedimiento
  } = useNewModal()

  const isPiezaSeparada =
    values[FormField.PROCEDIMIENTO]?.procedimientoTipologiaForm?.descripcion?.toLowerCase() ===
    'pieza separada'

  async function clearProcedimiento(): Promise<void> {
    clearProcedimientoFieldset()
    setFieldValue(FormField.PROCEDIMIENTO, ProcedimientoFormInitialvalues, false)
    setDisabledRemoveButton(true)
  }

  const checkIfDisabled = () => {
    return (
      (tipoMensaje === TipoMensaje.ESCRITO && !orden.value) ||
      (tipoMensaje !== TipoMensaje.ESCRITO && !ordenOrigen.value)
    )
  }

  function getProcedimientoButtons() {
    return (
      <>
        <LexnetIconButton
          title={t('tooltips.buscar_procedimiento')}
          handleClick={openProcedimiento}
          disabled={checkIfDisabled()}
        >
          <SearchIcon />
        </LexnetIconButton>
        <RemoveInfoButton
          clear={clearProcedimiento}
          disabled={disabledRemoveButton}
          firstDialogMessage={t('dialog.message.remove_procedimiento')}
        />
      </>
    )
  }

  function formSubmit(values: ProcedimientoFormValues) {
    setFieldValue(FormField.PROCEDIMIENTO, values, false)
    setFieldValue(
      FormField.PROCEDIMIENTO_DESCRIPCION,
      values[FormField.PROCEDIMIENTO_TIPO_FORM]?.texto,
      false
    )
    setFieldValue(
      FormField.PROCEDIMIENTO_REFERENCIA,
      `${values[FormField.PROCEDIMIENTO_NUMERO_FORM]}/${values[FormField.PROCEDIMIENTO_YEAR_FORM]}`,
      false
    )
    setFieldValue(
      FormField.PROCEDIMIENTO_NUMERO_PIEZA,
      values[FormField.PROCEDIMIENTO_NUMERO_PIEZA_FORM],
      false
    )
    setFieldValue(
      FormField.PROCEDIMIENTO_ORIGEN,
      values[FormField.PROCEDIMIENTO_TIPO_ORIGEN_FORM]?.texto,
      false
    )
    setFieldValue(
      FormField.PROCEDIMIENTO_ORIGEN_REFERENCIA,
      `${values[FormField.PROCEDIMIENTO_NUMERO_ORIGEN_FORM]}/${
        values[FormField.PROCEDIMIENTO_YEAR_ORIGEN_FORM]
      }`
    )

    closeProcedimiento()
  }

  useEffect(() => {
    if (ProcedimientoDescripcionField) {
      setDisabledRemoveButton(false)
    }
  }, [ProcedimientoDescripcionField])

  return (
    <ProcedimientoView
      isPiezaSeparada={isPiezaSeparada}
      isProcedimientoOpened={isProcedimientoOpened}
      closeProcedimiento={closeProcedimiento}
      getProcedimientoButtons={getProcedimientoButtons}
      formSubmit={formSubmit}
      tipoMensaje={tipoMensaje}
      checkIfDisabledField={checkIfDisabled}
      openProcedimiento={() => {
        if (!checkIfDisabled()) {
          openProcedimiento()
        }
      }}
    />
  )
}

export default Procedimiento
